import { CACHE_PRODUCT_LIST_VERSION } from 'constant/cache'
import httpRequest from 'httpRequest'
import {
    ProductContract,
    ItemContract,
    POSCombinedModel,
    ProductSlimModel,
    UnavailableItemsContract,
    ItemSlimContract,
    UpsertProductContract,
    EntityLink,
    ComboBuilderProduct,
    LinkedModifierSlim,
    ProductExportContract,
} from 'models'
import { ProductPriceListSlimModel } from 'models/ProductPriceListSlimModel'
import pathWithQuery from 'utilities/queryParams'

const PRODUCT_PREFIX = '/company/products'

type TPosProduct = {
    data: POSCombinedModel
    status: any
}

const productService = {
    getProducts(variantItem: boolean = false) {
        return httpRequest.get<ProductContract[]>(
            pathWithQuery({ variantItem }, PRODUCT_PREFIX),
        )
    },
    async getProductsWithVariants() {
        return httpRequest.get<ProductContract[]>(
            pathWithQuery({ variantItem: true }, PRODUCT_PREFIX),
        )
    },
    async getProductsWithVariantsAndCombo() {
        return httpRequest.get<ProductContract[]>(
            pathWithQuery(
                { variantItem: true, comboItem: true },
                PRODUCT_PREFIX,
            ),
        )
    },
    async getPOSProducts(): Promise<TPosProduct> {
        const apiRoute = `${PRODUCT_PREFIX}/pos`

        if (!window.caches) return httpRequest.get<POSCombinedModel>(apiRoute)

        const productListVersion = localStorage.getItem(
            CACHE_PRODUCT_LIST_VERSION,
        )
        const lastVersion = (
            await httpRequest.get<string>(`${PRODUCT_PREFIX}/cache/version`)
        ).data
        const cache = await caches.open('common')
        if (lastVersion === productListVersion) {
            const data = await cache.match(apiRoute)
            if (data)
                return Promise.resolve({ data: await data.json(), status: 200 })
        }
        var result = await httpRequest.get<POSCombinedModel>(apiRoute)
        await cache.put(apiRoute, new Response(JSON.stringify(result.data)))
        localStorage.setItem(CACHE_PRODUCT_LIST_VERSION, lastVersion)
        return Promise.resolve({ data: result.data, status: 200 })
    },
    getProductLookupList(
        variantItem: boolean = false,
        comboItem: boolean = false,
    ) {
        return httpRequest.get<ProductSlimModel[]>(
            pathWithQuery(
                { variantItem, comboItem },
                `${PRODUCT_PREFIX}/lookup-list`,
            ),
        )
    },
    getProduct(id: string) {
        return httpRequest.get<ItemContract>(`${PRODUCT_PREFIX}/${id}`)
    },
    getDependencies(productID: string) {
        return httpRequest.get<EntityLink>(
            `${PRODUCT_PREFIX}/dependency/${productID}`,
        )
    },
    createProduct(payload: ItemContract, isDepositAndCreditProduct: boolean) {
        return httpRequest.post<UpsertProductContract>(
            isDepositAndCreditProduct
                ? `${PRODUCT_PREFIX}?isDepositAndCreditProduct=true`
                : PRODUCT_PREFIX,
            payload,
        )
    },
    updateProduct(id: string, payload: ItemContract, isForceCombo: boolean) {
        return httpRequest.put<UpsertProductContract>(
            `${PRODUCT_PREFIX}/${id}`,
            payload,
            { params: { isForceCombo } },
        )
    },
    deleteProducts(productIDs: string[]) {
        return httpRequest.delete(`${PRODUCT_PREFIX}`, {
            params: { productIDs },
        })
    },
    updateFlag(
        productID: string,
        flagName: string,
        flagValue: boolean,
        isForceCombo: boolean = false,
    ) {
        return httpRequest.put(
            `${PRODUCT_PREFIX}/${productID}/update-flag`,
            {},
            {
                params: {
                    productID,
                    flagName,
                    flagValue,
                    isForceCombo,
                },
            },
        )
    },
    getUnavailableProducts: async () => {
        const apiRoute = `${PRODUCT_PREFIX}/unavailable-products`
        return httpRequest.get<UnavailableItemsContract>(apiRoute)
    },
    getProductForPriceList: () => {
        return httpRequest.get<ProductPriceListSlimModel[]>(
            `${PRODUCT_PREFIX}/pricelist-products`,
        )
    },
    getMenuBuilderProductByID: (itemID: string) =>
        httpRequest.get<ItemSlimContract>(
            `${PRODUCT_PREFIX}/menu-builder/${itemID}`,
        ),
    getCountAppliedModifierToAllProducts: () =>
        httpRequest.get<{ [key: string]: number }>(
            `${PRODUCT_PREFIX}/modifier-group-count`,
        ),
    getProductModifiers: (productID: string) =>
        httpRequest.get<LinkedModifierSlim[]>(
            `${PRODUCT_PREFIX}/${productID}/modifiers`,
        ),
    importProduct: (formData: FormData) =>
        httpRequest.post(`${PRODUCT_PREFIX}/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    initLocationData: (productID: string) =>
        httpRequest.post(`${PRODUCT_PREFIX}/init-location-data/${productID}`),
    getComboBuilderProducts: () =>
        httpRequest.get<ComboBuilderProduct[]>(
            `${PRODUCT_PREFIX}/for-combo-builder`,
        ),
    getExportProduct: () =>
        httpRequest.get<ProductExportContract[]>(`${PRODUCT_PREFIX}/export`),
}

export default productService
